import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, X } from "lucide-react";

import { Dialog } from "@/components/ui/dialog";
import { getThumbnailReviewerState, setThumbnailReviewerState } from "./state";
import { useSelector } from "@legendapp/state/react";

export const UsageLimitModal: React.FC = () => {
  const { t } = useTranslation();
  const isOpen = useSelector(getThumbnailReviewerState.isUsageLimitModalOpen);
  const type = useSelector(getThumbnailReviewerState.usageLimitModalType);
  const remainingCount = useSelector(getThumbnailReviewerState.remainingUsageCount);

  const closeModal = () => {
    setThumbnailReviewerState.closeUsageLimitModal();
  };

  const getModalContent = () => {
    switch (type) {
      case "exceeded":
        return {
          title: t("thumbnailReviewer.upload.usageLimitTitle"),
          message: t("thumbnailReviewer.upload.usageLimitExceeded"),
          icon: <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />,
          buttonText: t("thumbnailReviewer.upload.usageLimitButton"),
          buttonClass: "bg-red-600 hover:bg-red-700",
        };
      case "remaining":
        return {
          title: t("thumbnailReviewer.upload.remainingTriesTitle"),
          message: t("thumbnailReviewer.upload.remainingTries", {
            count: remainingCount,
          }),
          icon: <Info className="h-6 w-6 text-blue-600" aria-hidden="true" />,
          buttonText: t("thumbnailReviewer.upload.continueButton"),
          buttonClass: "bg-blue-600 hover:bg-blue-700",
        };
      case "last":
        return {
          title: t("thumbnailReviewer.upload.lastTryTitle"),
          message: t("thumbnailReviewer.upload.lastTry"),
          icon: <Info className="h-6 w-6 text-yellow-600" aria-hidden="true" />,
          buttonText: t("thumbnailReviewer.upload.continueButton"),
          buttonClass: "bg-yellow-600 hover:bg-yellow-700",
        };
      default:
        return {
          title: "",
          message: "",
          icon: null,
          buttonText: t("thumbnailReviewer.upload.closeButton"),
          buttonClass: "bg-gray-600 hover:bg-gray-700",
        };
    }
  };

  const content = getModalContent();

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
        {/* Add a custom overlay */}
        {isOpen && <div className="fixed inset-0 z-40 bg-black/80" aria-hidden="true" />}

        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="absolute right-4 top-4">
              <button
                type="button"
                className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={closeModal}
              >
                <span className="sr-only">Close</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="flex items-center">
              <div className="mr-4 flex-shrink-0">{content.icon}</div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">{content.title}</h3>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-500">{content.message}</p>
            </div>

            <div className="mt-6">
              <button
                type="button"
                className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${content.buttonClass}`}
                onClick={closeModal}
              >
                {content.buttonText}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
